// First-run welcome state for the investor portal.
//
// Completion is stored per user in localStorage under the same key the
// original dashboard onboarding modal used (`eb5-onboarding-seen-<userId>`),
// so investors who already walked through the old flow are not shown the new
// one. The store is intentionally device-local: the passkey step is
// device-bound, so re-offering the (short) flow on a new device is desirable.
//
// A tiny external store lets Account Settings request a replay from anywhere
// in the tree without threading callbacks through the App shell.

import { useCallback, useEffect, useSyncExternalStore } from "react";

function storageKey(userId: string): string {
  return `eb5-onboarding-seen-${userId}`;
}

export function hasCompletedInvestorWelcome(userId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(storageKey(userId)) === "true";
  } catch {
    return true;
  }
}

export function markInvestorWelcomeCompleted(userId: string): void {
  try {
    localStorage.setItem(storageKey(userId), "true");
  } catch {
    // Private mode / quota — the flow simply shows again next visit.
  }
}

/** Test-only helper: forget completion for a user. */
export function resetInvestorWelcome(userId: string): void {
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();
let version = 0;
let relaunchRequested = false;

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return version;
}

/** Re-open the welcome flow (used by the Account Settings "Replay" button). */
export function requestInvestorWelcomeRelaunch(): void {
  relaunchRequested = true;
  emit();
}

export function isInvestorWelcomeRelaunchRequested(): boolean {
  return relaunchRequested;
}

const ELIGIBILITY_WIZARD_PATH = "/dashboard/accreditation";

/**
 * The eligibility questionnaire owns this URL. A first-run investor who
 * lands here — including the hop from /investor-verification — must be
 * able to fill the form. The welcome dialog yields on this path only.
 */
export function isEligibilityWizardPath(location: string): boolean {
  const path = location.split("?")[0]?.split("#")[0] ?? location;
  return path === ELIGIBILITY_WIZARD_PATH || path.startsWith(`${ELIGIBILITY_WIZARD_PATH}/`);
}

/**
 * Shell visibility for the first-run welcome.
 *
 * `pending` stays true on the eligibility wizard so the standalone terms
 * gate and passkey prompt stay deferred (the welcome still owes those
 * steps). `showDialog` is false there so the dialog does not intercept
 * clicks. Everywhere else in the portal shell, a pending welcome shows.
 */
export function investorWelcomeShellState(input: {
  isPortalInvestor: boolean;
  inPortalShell: boolean;
  location: string;
  welcomeOpen: boolean;
  mustChangePassword: boolean;
}): { pending: boolean; showDialog: boolean } {
  const pending =
    input.isPortalInvestor &&
    input.inPortalShell &&
    input.welcomeOpen &&
    !input.mustChangePassword;
  return {
    pending,
    showDialog: pending && !isEligibilityWizardPath(input.location),
  };
}

/**
 * Whether the welcome flow should be open for `userId`, plus a `complete`
 * callback that persists completion and clears any relaunch request.
 * Pass `null` when there is no eligible investor signed in.
 */
export function useInvestorWelcome(userId: string | null): {
  open: boolean;
  complete: () => void;
} {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // A replay requested by one session must not leak into the next sign-in
  // in the same tab.
  useEffect(() => {
    if (!userId && relaunchRequested) {
      relaunchRequested = false;
      emit();
    }
  }, [userId]);

  const open =
    !!userId && (relaunchRequested || !hasCompletedInvestorWelcome(userId));

  const complete = useCallback(() => {
    if (userId) markInvestorWelcomeCompleted(userId);
    relaunchRequested = false;
    emit();
  }, [userId]);

  return { open, complete };
}
